import React from 'react';

/**
 * Keeps a scrolling message list on its newest message. The list starts at the bottom whenever it mounts (a sheet
 * reopening, a tab switch), and when it gets shorter (a phone keyboard opening) it stays at the bottom, unless the
 * person had scrolled up to read older messages. Returns a callback ref for the list, which also fills `listRef`.
 * New messages still scroll the list themselves.
 *
 * "At the bottom" is judged against the height the list had before it resized. A scroll event would arrive a frame
 * late: when a sheet shrinks in two steps, the event from the first step's scroll reads the second step's height and
 * reports the person as scrolled up.
 */
export function useKeepNewestInView<T extends HTMLElement>(listRef: React.MutableRefObject<T | null>): React.RefCallback<T> {
  const detachRef = React.useRef<(() => void) | null>(null);
  return React.useCallback((list: T | null) => {
    detachRef.current?.();
    detachRef.current = null;
    listRef.current = list;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    if (typeof ResizeObserver === 'undefined') return;
    let height = list.clientHeight;
    const observer = new ResizeObserver(() => {
      const wasAtBottom = list.scrollHeight - list.scrollTop - height < 24;
      height = list.clientHeight;
      if (wasAtBottom) list.scrollTop = list.scrollHeight;
    });
    observer.observe(list);
    detachRef.current = () => observer.disconnect();
  }, [listRef]);
}
